import { setCookie } from "../../utils/common";
import axiosInstance from "../api/axios";
import { env } from "../../utils/env";
import { getAppId } from "../../constants/appIdentity";

// Log-only mirror of the axios base URL; keep the fallback in step with
// services/api/axios.js or the console reports a port nothing talks to.
const API_BASE_URL = env('REACT_APP_API_BASE_URL') || 'http://localhost:8000/api';

// Add console log to check API URL
console.log('Current API Base URL:', API_BASE_URL);

// Login user
export const loginUser = async (credentials) => {
  try {
    const response = await axiosInstance.post('/login', credentials);
    return response; 
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const getLoginDetails = async (username) => {
  try {
    const response = await axiosInstance.get('/login', {
      params: { username },
      
    });
    const userDetails = Array.isArray(response) ? response[0] : response;
    
    if (userDetails?.loginIdentifier) {
     
      setCookie('loginIdentifier', userDetails.loginIdentifier);
    }

    return userDetails;
  } catch (error) {
    console.error('Error getting login details:', error);
    throw error;
  }
};


export const createNewAccount = async (login_username) => {
  try {
    const response = await axiosInstance.post('/login/new-account',null, {
      params: { login_username }
    });
    return response;
  } catch (error) {
    console.error('Error creating new account:', error);
    throw error;
  }
};

export const checkPassword = async (username, password) => {
    // Nothing about a login attempt is logged: the username identifies a person, and the response
    // below carries their contact details and personas. The axios interceptor encrypts `password`
    // on the way out.
    try {
      // appId is also sent in the body, not just as a header: this is one of the
      // unauthenticated calls, and a stripped header here means the backend makes
      // the wrong allow/block decision. On web the backend prefers the browser's
      // Origin over both anyway.
      const appId = getAppId();
      const response = await axiosInstance.post('/login/check-password', {
        username,
        password,
        // Omitted when unset (local dev) so the backend applies no filtering.
        ...(appId ? { appId } : {}),
      });
      return response  
    } catch (error) {
      console.error('Error checking password:', error);
      throw error; 
    }
  };
  


  /**
   * GET /login/identifier-links
   * Returns every identifier link associated with the calling login.
   * Used by the customer-picker flow when a single login is wired to
   * multiple developer/customer organizations. Requires a valid bearer
   * token (the axios interceptor pulls it from the accessToken cookie).
   */
  export const fetchLoginIdentifierLinks = async () => {
    try {
      const response = await axiosInstance.get('/login/identifier-links');
      return response;
    } catch (error) {
      console.error('Error fetching login identifier links:', error);
      throw error;
    }
  };

  /**
   * POST /login/select-identifier
   * Switches the active identifier for this login and re-issues a JWT.
   * Returns the same shape as /login/check-password (ResponseLoginDetailsData)
   * — caller is responsible for swapping the token + userInfo in storage.
   */
  export const selectLoginIdentifier = async ({ linkId, remember = false }) => {
    try {
      const response = await axiosInstance.post('/login/select-identifier', {
        linkId,
        remember,
      });
      return response;
    } catch (error) {
      console.error('Error selecting login identifier:', error);
      throw error;
    }
  };

  // Rehydrate the full identity from the bearer token.
//
// The _USER_AUTH_ cookie deliberately carries only the fields a cold load needs
// to bootstrap (see slimUserInfo) — storing the whole object there silently
// overflowed the ~4KB cookie limit. Opening the app in a NEW tab therefore starts
// without loginIdentifierDetails, and this fills them back in.
export const fetchEntityInfo = async () => {
  const response = await axiosInstance.get('/login/entity-info');
  return response;
};

// Step 1 of a forgotten-password reset. Always resolves — the backend answers 202
// whether or not the account exists, so this cannot be used to discover usernames.
export const requestPasswordReset = async (username) => {
  const response = await axiosInstance.post('/login/forgot-password', { username });
  return response;
};

// Step 2 — redeem the emailed one-time code and set the new password.
export const resetPassword = async (username, otp, password) => {
  const response = await axiosInstance.post('/login/reset-password', { username, otp, password });
  return response;
};

export const setPassword = async (username, password) => {
    try {
      const response = await axiosInstance.post('/login/set-password', {
        username,
        password
      });
      return response;
    } catch (error) {
      console.error('Error setting password:', error);
      throw error;
    }
  };

/**
 * Close the signed-in account. Irreversible.
 *
 * Acts only on the caller's own login — the server reads it off the session and the body
 * carries no id, so there is nothing here for a caller to point at someone else. `password`
 * is re-checked server-side even though the request is authenticated, and is encrypted in
 * transit by the axios interceptor (REQUEST_SENSITIVE_FIELDS in utils/transitPayload.js).
 *
 * `confirmed` is a deliberate-action gate: the server answers 422 without it, so a stray
 * call cannot close an account.
 *
 * Resolves to `{ message, retentionNotice }`. Show the notice — it is what tells the person
 * which records we are legally required to keep, which Play requires us to disclose.
 */
export const deleteAccount = async (password) => {
  const response = await axiosInstance.post('/login/delete-account', {
    password,
    confirmed: true,
  });
  return response;
};

  export const getCustomers = async ({ customerType }) => {
    try {
      const params = { customerType };
      const response = await axiosInstance.get(`/customers/subscription`, { params });
      return response;
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  };
  export const getCustomerEarnings = async ({ startDate, endDate }) => {
    try {
        const response = await axiosInstance.get(`/subscription/customer/earnings`, {
            params: {
               startDate,
               endDate
             },
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching customer earnings", error);
        throw error;
    }
};
